import { useMemo, useState } from "react";
import { debounce, every, get, orderBy } from "lodash";
import { useSortAndPagination } from "@/shared/utils/hooks/useSortAndPagination";
import { useListFilters } from "@/shared/hooks/useListFilters";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import {
  Person,
  PersonRole,
  PeopleFilter,
  usePeopleQuery,
  SurveyCompletion,
} from "@/features/people/graphql/generated";
import { useAccountMembers } from "@/features/workspace/hooks/useAccountMembers";
import { ResourceEnum } from "@/shared/constants/resources";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import { getPersonRoleObject } from "@/shared/utils/selectors/getRolesAndAccess";
import { useAssessmentSurveys } from "@/features/profile/hooks/useAssessmentSurveys";
import { getIsSurveysCompleted } from "@/shared/utils/selectors/getSurveyCompletion";
import { AccessLevel, RoleType } from "@/shared/constants";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import {
  ASSESSMENT_SURVEY_TYPES,
  AssessmentSurveyType,
} from "@/shared/constants/assessmentSurveys";
import { AssessmentOrder } from "@/features/auth/graphql/generated";
import { p } from "react-router/dist/development/index-react-server-client-DKvU8YRr";

interface UseSearchTalentPoolOptions {
  /**
   * When true and user has FULL account access, searches the entire account talent pool.
   * When false (default), always searches organization-specific talent pool.
   */
  enableFullAccountSearch?: boolean;
}

/**
 * Search talent pool hook that determines scope based on user permissions and options.
 * - When enableFullAccountSearch is true AND user has FULL account access: searches all account members
 * - Otherwise: searches organization-specific members only
 */
export const useSearchTalentPool = (
  options: UseSearchTalentPoolOptions = {}
) => {
  const { enableFullAccountSearch = false } = options;

  const { organization: selectedOrg } = useOrganisation();
  const { account } = useAccount();
  const { validateAccessToResource } = useViewerPermissions();

  // Check if user has full account access using existing permission system
  const userHasFullAccountAccess = validateAccessToResource({
    resourceType: ResourceEnum.account,
    accessLevel: AccessLevel.full,
  });

  // Only use account-wide search if explicitly enabled AND user has permission
  const useAccountWideSearch =
    enableFullAccountSearch && userHasFullAccountAccess;

  // Build filter based on search scope:
  // - Account-wide search: empty filter returns all account members
  // - Otherwise: org_id filter returns organization members only
  const filter = useMemo<PeopleFilter>(() => {
    if (useAccountWideSearch) {
      return {} as PeopleFilter;
    }
    return { org_id: selectedOrg.id };
  }, [useAccountWideSearch, selectedOrg.id]);

  const shouldSkip = useAccountWideSearch ? !account.id : !selectedOrg.id;

  const { data, loading } = usePeopleQuery({
    variables: { filter },
    skip: shouldSkip,
    fetchPolicy: "cache-and-network",
  });

  const people = (data?.people || []) as Person[];

  // Determine resource context for role lookups
  const resourceType = useAccountWideSearch
    ? ResourceEnum.account
    : ResourceEnum.organization;
  const resourceId = useAccountWideSearch ? account.id : selectedOrg.id;

  const search = useSearchPeople(people, resourceType, resourceId!);

  return {
    loading,
    isAccountWideSearch: useAccountWideSearch,
    ...search,
  };
};

export const useSearchAccountTalentPool = () => {
  const { accountMembers, loading } = useAccountMembers();

  const search = useSearchPeople(
    accountMembers as Person[],
    ResourceEnum.account
  );

  return {
    loading,
    ...search,
  };
};

export const useSearchPeople = (
  people: Person[] = [],
  resourceType: ResourceEnum = ResourceEnum.organization,
  resourceId?: number
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { organization } = useOrganisation();
  const { getAccountAssessmentSurveys, account } = useAccount();

  const { filters, setFilter, clearFilter, clearAllFilters, hasActiveFilters } =
    useListFilters({
      filterKeys: ["role", "status", "access_level"],
      multiSelectKeys: [],
    });

  const {
    page,
    limit,
    sort,
    handleSortChange,
    handleLimitChange,
    handlePageChange,
  } = useSortAndPagination({
    initialLimit: 25,
    initialSort: { key: "name", direction: "asc" },
    initialPage: 1,
  });

  const handleSearch = debounce((searchValue: string) => {
    setSearchTerm(searchValue);
    handlePageChange(1);
  }, 500);

  const handleFilterChange = (filterKey: string, value?: string) => {
    if (value === "all" || !value) {
      clearFilter(filterKey);
    } else {
      setFilter(filterKey, value);
    }
  };

  const getPersonRole = (person: Person) => {
    const roleResourceId =
      resourceId ||
      (resourceType === ResourceEnum.organization
        ? organization.id!
        : account.id!);
    return getPersonRoleObject(
      person.roles! as PersonRole[],
      resourceType,
      roleResourceId
    );
  };

  const accountSurveyTypes = getAccountAssessmentSurveys().map(
    (survey) => survey.type as AssessmentSurveyType
  );

  const getPersonStatus = (person: Person) => {
    if (person.is_contact) {
      return "contact";
    }
    if (person.invite) {
      return "invited";
    }

    if (
      getIsSurveysCompleted(
        person?.completed_dim_types as AssessmentSurveyType[],
        accountSurveyTypes
      )
    ) {
      return "complete";
    }

    return "pending";
  };

  const fullData = useMemo(
    () =>
      people.map((person) => {
        const personRole = getPersonRole(person);
        const isWorkspaceContactOnOrg =
          resourceType === ResourceEnum.organization && !!person.is_contact;

        // Workspace contacts on the org people page should not be editable.
        // We keep displayed role/access as-is, but force editability checks to treat them as CONTACT.
        const roleObject = isWorkspaceContactOnOrg
          ? ({ ...(personRole || {}), role: RoleType.contact } as PersonRole)
          : personRole;
        return {
          ...person,
          role: personRole?.role,
          roleObject,
          access_level: personRole?.access_level,
          status: getPersonStatus(person),
          profile_completed: getIsSurveysCompleted(
            person?.completed_dim_types as AssessmentSurveyType[],
            accountSurveyTypes
          ),
        };
      }),
    [people, resourceType, resourceId]
  );

  const data = useMemo(() => {
    const sortedData = orderBy(
      fullData,
      (person) => {
        if (sort.key === "status") {
          return person?.status;
        }

        return get(person, sort.key, "");
      },
      [sort.direction] as any
    );

    return sortedData.filter((person) => {
      // Apply all filters simultaneously (AND logic)
      const matchesRole = !filters.role || person.role === filters.role;
      const matchesStatus = !filters.status || person.status === filters.status;
      const matchesAccessLevel =
        !filters.access_level || person.access_level === filters.access_level;
      const matchesSearch =
        !searchTerm ||
        person?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person?.email?.toLowerCase().includes(searchTerm.toLowerCase());

      return (
        matchesRole && matchesStatus && matchesAccessLevel && matchesSearch
      );
    });
  }, [searchTerm, sort, filters, fullData]);

  // Add pagination
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedData = data.slice(startIndex, endIndex);

  const totalPages = data?.length / limit;

  return {
    data: paginatedData,
    fullData: data,
    searchTerm,
    handleSearch,
    sort,
    page,
    limit,
    totalPages,
    filters,
    handleFilterChange,
    clearFilter,
    clearAllFilters,
    hasActiveFilters,
    handleSortChange,
    handleLimitChange,
    handlePageChange,
    setSearchTerm,
  };
};
